import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { VariableBlobExtension, convertBlobPatternsToHtml, convertHtmlToBlobPatterns } from '../extensions/VariableBlobExtension';
import { VariableBlobDropdown } from './VariableBlobDropdown';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  clauseId?: number;
  enableVariables?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Saisissez votre texte...',
  clauseId,
  enableVariables = false,
}) => {
  // Use refs to always have access to the latest values in callbacks
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const enableVariablesRef = useRef(enableVariables);
  useEffect(() => {
    enableVariablesRef.current = enableVariables;
  }, [enableVariables]);

  // Track if the change came from internal editing to avoid resetting cursor
  const isInternalChange = useRef(false);

  // State for variable dropdown
  const [variableDropdownOpen, setVariableDropdownOpen] = useState(false);

  // Pre-process value to convert [●...●] patterns to HTML nodes
  const processedValue = useMemo(() => {
    if (!enableVariables) return value;
    return convertBlobPatternsToHtml(value);
  }, [value, enableVariables]);

  // Build extensions list
  const extensions = useMemo(() => {
    const baseExtensions = [
      StarterKit,
      Placeholder.configure({
        placeholder,
      }),
    ];
    if (enableVariables) {
      return [...baseExtensions, VariableBlobExtension];
    }
    return baseExtensions;
  }, [placeholder, enableVariables]);

  const editor = useEditor({
    extensions,
    content: processedValue,
    onUpdate: ({ editor }) => {
      let html = editor.getHTML();
      // Convert spans back to [●name●] format for storage
      // Use ref to always get the current value, avoiding stale closure
      if (enableVariablesRef.current) {
        html = convertHtmlToBlobPatterns(html);
      }
      // Mark this as an internal change to prevent cursor reset in sync effect
      isInternalChange.current = true;
      onChangeRef.current(html);
    },
  }, [extensions]);

  // Sync external value changes using JSON comparison for robustness
  useEffect(() => {
    if (!editor) return;

    // Skip sync if this change came from internal editing (user typing)
    // This prevents cursor from jumping to end when typing
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // Only proceed if HTML strings differ (quick check)
    if (editor.getHTML() === processedValue) return;

    // Deep compare by checking if the actual content differs
    const currentContent = editor.getJSON();
    editor.commands.setContent(processedValue, { emitUpdate: false });
    const newContent = editor.getJSON();

    // If content is actually the same (just HTML normalization differences), restore original
    if (JSON.stringify(currentContent) === JSON.stringify(newContent)) {
      editor.commands.setContent(currentContent, { emitUpdate: false });
    }
  }, [processedValue, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="rich-text-editor-container">
      <div className="rich-text-toolbar">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`rich-text-toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
          title="Gras"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
            <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
          </svg>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`rich-text-toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
          title="Italique"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="4" x2="10" y2="4"></line>
            <line x1="14" y1="20" x2="5" y2="20"></line>
            <line x1="15" y1="4" x2="9" y2="20"></line>
          </svg>
        </button>

        <div className="rich-text-toolbar-divider" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`rich-text-toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
          title="Liste à puces"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="8" y1="6" x2="21" y2="6"></line>
            <line x1="8" y1="12" x2="21" y2="12"></line>
            <line x1="8" y1="18" x2="21" y2="18"></line>
            <circle cx="3" cy="6" r="1" fill="currentColor"></circle>
            <circle cx="3" cy="12" r="1" fill="currentColor"></circle>
            <circle cx="3" cy="18" r="1" fill="currentColor"></circle>
          </svg>
        </button>

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`rich-text-toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
          title="Liste numérotée"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="10" y1="6" x2="21" y2="6"></line>
            <line x1="10" y1="12" x2="21" y2="12"></line>
            <line x1="10" y1="18" x2="21" y2="18"></line>
            <path d="M4 6h1v4"></path>
            <path d="M4 10h2"></path>
            <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"></path>
          </svg>
        </button>

        <div className="rich-text-toolbar-divider" />

        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`rich-text-toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
          title="Titre"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12h8"></path>
            <path d="M4 18V6"></path>
            <path d="M12 18V6"></path>
            <path d="M17 10v8"></path>
            <path d="M17 10l3-2"></path>
            <path d="M17 10l-3-2"></path>
          </svg>
        </button>

        {enableVariables && (
          <>
            <div className="rich-text-toolbar-divider" />
            <div className="rich-text-toolbar-variable-container">
              <button
                type="button"
                onClick={() => setVariableDropdownOpen(!variableDropdownOpen)}
                className={`rich-text-toolbar-btn rich-text-toolbar-btn-variable ${variableDropdownOpen ? 'active' : ''}`}
                title="Ajouter une variable"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                </svg>
                <span>Variable</span>
              </button>
              <VariableBlobDropdown
                editor={editor}
                clauseId={clauseId}
                isOpen={variableDropdownOpen}
                onClose={() => setVariableDropdownOpen(false)}
              />
            </div>
          </>
        )}
      </div>

      <div className="rich-text-content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
